import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

interface LandingPageProps {
  onGetStarted: () => void;
  onLogin: () => void;
}

export default function LandingPage({ onGetStarted, onLogin }: LandingPageProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const [showTarificationTooltip, setShowTarificationTooltip] = useState(false)
  const tarificationButtonRef = useRef<HTMLButtonElement>(null)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [currentPage, setCurrentPage] = useState<'privacy' | 'terms'>('privacy')
  const [selectedImage, setSelectedImage] = useState<string | null>('/luck.jpg')
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)
  const [showPlateformeDropdown, setShowPlateformeDropdown] = useState(false)
  const [showSolutionsDropdown, setShowSolutionsDropdown] = useState(false)
  const plateformeButtonRef = useRef<HTMLButtonElement>(null)
  const solutionsButtonRef = useRef<HTMLButtonElement>(null)

  const handleGoogleAuth = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Check user limit before OAuth
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!
      const checkLimitResponse = await fetch(`${supabaseUrl}/functions/v1/check-user-limit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      })
      
      if (!checkLimitResponse.ok) {
        const errorText = await checkLimitResponse.text()
        console.error('Error checking user limit:', checkLimitResponse.status, errorText)
        // Block signup if limit check fails (fail closed for security)
        setError('Impossible de vérifier la limite d\'utilisateurs. Veuillez réessayer plus tard.')
        setIsLoading(false)
        return
      }
      
      const limitData = await checkLimitResponse.json()
      
      if (!limitData.canSignUp) {
        setError('La limite de 100 utilisateurs a été atteinte. Veuillez réessayer plus tard.')
        setIsLoading(false)
        return
      }
      
      // Proceed with OAuth
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      })
      
      if (error) {
        if (error.message.includes('access_denied') || error.message.includes('testing')) {
          setError('L\'application Google OAuth est en mode test. Veuillez contacter l\'administrateur pour obtenir l\'accès.')
        } else {
          setError(error.message)
        }
        setIsLoading(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la connexion')
      setIsLoading(false)
    }
  }

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!showPassword) {
      setShowPassword(true)
      return
    }
    
    setIsEmailLoading(true)
    setError(null)
    
    try {
      // Check user limit before signup
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!
      const checkLimitResponse = await fetch(`${supabaseUrl}/functions/v1/check-user-limit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      })
      
      if (!checkLimitResponse.ok) {
        const errorText = await checkLimitResponse.text()
        console.error('Error checking user limit:', checkLimitResponse.status, errorText)
        // Block signup if limit check fails (fail closed for security)
        throw new Error('Impossible de vérifier la limite d\'utilisateurs. Veuillez réessayer plus tard.')
      }
      
      const limitData = await checkLimitResponse.json()
      
      if (!limitData.canSignUp) {
        throw new Error('La limite de 100 utilisateurs a été atteinte. Veuillez réessayer plus tard.')
      }
      
      // Proceed with signup
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      })
      
      if (error) throw error
      
      setEmailSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setIsEmailLoading(false)
    }
  }

  const handleResendEmail = async () => {
    setIsResending(true)
    setResendSuccess(false)
    setError(null)
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      })
      
      if (error) throw error
      
      setResendSuccess(true)
      setTimeout(() => setResendSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors du renvoi')
    } finally {
      setIsResending(false)
    }
  }

  const handleResetForm = () => {
    setEmail('')
    setPassword('')
    setShowPassword(false)
    setEmailSent(false)
    setError(null)
    setResendSuccess(false)
  }

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tarificationButtonRef.current && !tarificationButtonRef.current.contains(event.target as Node)) {
        setShowTarificationTooltip(false)
      }
      if (plateformeButtonRef.current && !plateformeButtonRef.current.contains(event.target as Node)) {
        setShowPlateformeDropdown(false)
      }
      if (solutionsButtonRef.current && !solutionsButtonRef.current.contains(event.target as Node)) {
        setShowSolutionsDropdown(false)
      }
    }

    if (showTarificationTooltip || showPlateformeDropdown || showSolutionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showTarificationTooltip, showPlateformeDropdown, showSolutionsDropdown])

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-[#2C2C2C] z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left - Logo and Navigation */}
            <div className="flex items-center gap-8">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img 
                src="/logo_supabase_green.png" 
                alt="Avocat AI" 
                className="h-8 w-auto"
              />
                <span className="text-xl font-semibold text-white">Avocat AI</span>
              </div>

              {/* Navigation Links */}
              <div className="hidden md:flex items-center gap-6">
                <div className="relative" ref={plateformeButtonRef}>
                  <button 
                    className="text-sm font-medium text-[#3ECF8E] hover:text-[#34B27B] transition-colors"
                    onClick={() => {
                      setShowPlateformeDropdown(!showPlateformeDropdown)
                      setShowSolutionsDropdown(false)
                    }}
                  >
                    Plateforme
                  </button>
                  {showPlateformeDropdown && (
                    <div className="absolute top-full left-0 mt-2 bg-black border border-[#2C2C2C] rounded-lg shadow-xl z-50 min-w-[280px] py-2">
                      <ul className="space-y-1">
                        <li>
                          <button
                            onClick={() => setShowPlateformeDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Réponses juridiques instantanées
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => setShowPlateformeDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Notes intelligentes
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => setShowPlateformeDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Mon Vault
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                <div className="relative" ref={solutionsButtonRef}>
                  <button 
                    className="text-sm font-medium text-[#3ECF8E] hover:text-[#34B27B] transition-colors"
                    onClick={() => {
                      setShowSolutionsDropdown(!showSolutionsDropdown)
                      setShowPlateformeDropdown(false)
                    }}
                  >
                    Solutions
                  </button>
                  {showSolutionsDropdown && (
                    <div className="absolute top-full left-0 mt-2 bg-black border border-[#2C2C2C] rounded-lg shadow-xl z-50 min-w-[320px] py-2">
                      <ul className="space-y-1">
                        <li>
                          <button
                            onClick={() => setShowSolutionsDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Recherche d'articles en quelques secondes
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => setShowSolutionsDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Synthèse automatique de vos conversations
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => setShowSolutionsDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Analyse juridique
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => setShowSolutionsDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Organisation de vos dossiers et historique
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => setShowSolutionsDropdown(false)}
                            className="w-full text-left px-4 py-2.5 text-sm text-[#808080] hover:text-[#3ECF8E] transition-colors"
                          >
                            Assistant IA disponible 24h/24
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                <div className="relative" ref={tarificationButtonRef}>
                  <button 
                    className="text-sm font-medium text-[#3ECF8E] hover:text-[#34B27B] transition-colors"
                    onMouseEnter={() => setShowTarificationTooltip(true)}
                    onMouseLeave={() => setShowTarificationTooltip(false)}
                  >
                    Tarification
                  </button>
                  {showTarificationTooltip && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-[#1A1A1A] border border-[#2C2C2C] rounded-lg shadow-lg z-50 whitespace-nowrap">
                      <p className="text-sm text-white">La plateforme est actuellement en période d'essai.</p>
                      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-[#1A1A1A] border-l border-t border-[#2C2C2C] rotate-45"></div>
                    </div>
                  )}
                </div>
                <a 
                  href="/terms"
                  className="text-sm font-medium text-[#3ECF8E] hover:text-[#34B27B] transition-colors"
                >
                  Conditions d'utilisation
                </a>
                <a 
                  href="/privacy"
                  className="text-sm font-medium text-[#3ECF8E] hover:text-[#34B27B] transition-colors"
                >
                  Politique de confidentialité
                </a>
              </div>
            </div>

            {/* Right - Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  // Do nothing for now, just keep it clickable
                }}
                className="px-4 py-2 text-sm font-medium text-[#3ECF8E] border border-[#3ECF8E] rounded-lg hover:bg-[#3ECF8E]/10 transition-colors"
              >
                Contacter notre support
              </button>
              <button
                onClick={() => {
                  // Focus the email input field
                  setTimeout(() => {
                    emailInputRef.current?.focus()
                  }, 100)
                }}
                className="px-5 py-2 text-sm font-medium text-white bg-[#3ECF8E] rounded-lg hover:bg-[#34B27B] transition-colors"
              >
                Essayez Avocat
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content - Split Layout */}
      <div className="pt-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            {/* Left Side - Title and Login Form */}
            <div className="w-full max-w-md mx-auto lg:mx-0 flex flex-col">
              {/* Large Title */}
              <h1 className="text-xl md:text-2xl font-serif font-bold text-white mb-8 leading-tight max-w-lg text-center mx-auto">
                La première plateforme juridique IA en Tunisie — performante, éco-optimisée et responsable
              </h1>

              {/* Login Form */}
              <div className="bg-[#1A1A1A] rounded-2xl border border-[#2C2C2C] p-8 shadow-xl">

                {/* Error Message */}
                {error && !emailSent && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                    {error}
                </div>
                )}

                {/* Email Sent Screen */}
                {emailSent ? (
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-white mb-3">
                      Consultez votre boîte de réception
            </h2>
                    <p className="text-sm text-[#B3B3B3] leading-relaxed mb-6">
                      Cliquez sur le lien que nous avons envoyé à l'adresse{' '}
                      <span className="font-semibold text-white">{email}</span>{' '}
                      pour finaliser la configuration de votre compte.
                    </p>

                    {resendSuccess && (
                      <div className="mb-4 p-3 bg-[#3ECF8E]/10 border border-[#3ECF8E]/30 rounded-lg text-sm text-[#3ECF8E]">
                        E-mail renvoyé avec succès !
          </div>
                    )}

                    {error && (
                      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                        {error}
              </div>
                    )}

                    <a
                      href="https://mail.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-3 px-4 bg-[#3ECF8E] text-white font-medium text-[15px] rounded-lg hover:bg-[#34B27B] hover:shadow-md transition-all mb-4 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                </svg>
                      <span>Ouvrir Gmail</span>
                    </a>

                    <p className="text-sm text-[#B3B3B3] mb-3">
                      Vous ne trouvez aucun e-mail ?{' '}
                      <button
                        onClick={handleResendEmail}
                        disabled={isResending}
                        className="text-[#3ECF8E] hover:underline font-medium disabled:opacity-50"
                      >
                        {isResending ? 'Envoi en cours...' : 'Envoyons-en un autre'}
                      </button>
                    </p>

                    <p className="text-sm text-[#808080]">
                      Mauvaise adresse ?{' '}
                      <button
                        onClick={handleResetForm}
                        className="text-[#3ECF8E] hover:underline font-medium"
                      >
                        Déconnexion
                      </button>
                      {' '}pour vous connecter avec une autre adresse e-mail.
              </p>
            </div>
                ) : (
                  <>
                    {/* Google Button */}
                    <div className="mb-4">
                      <button
                        onClick={handleGoogleAuth}
                        type="button"
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#1F1F1F] border border-[#2C2C2C] rounded-lg hover:bg-[#252525] hover:border-[#3ECF8E]/40 transition-all font-medium text-[15px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                        )}
                        <span>Continuer avec Google</span>
                      </button>
            </div>

                    {/* Divider */}
                    <div className="relative my-5">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#2C2C2C]"></div>
              </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-3 bg-[#1A1A1A] text-[#808080] font-medium uppercase tracking-wide">OU</span>
          </div>
        </div>

                    {/* Email Form */}
                    <form onSubmit={handleEmailContinue} className="space-y-3">
                      <input
                        ref={emailInputRef}
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Saisissez votre e-mail"
                        required
                        disabled={isEmailLoading}
                        className="w-full px-4 py-3 bg-[#1F1F1F] border border-[#2C2C2C] rounded-lg focus:ring-2 focus:ring-[#3ECF8E] focus:border-transparent outline-none transition-all text-[15px] text-white placeholder-[#808080] disabled:opacity-50"
                      />

                      {showPassword && (
                        <input
                          type="password"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Saisissez votre mot de passe"
                          required
                          disabled={isEmailLoading}
                          minLength={6}
                          className="w-full px-4 py-3 bg-[#1F1F1F] border border-[#2C2C2C] rounded-lg focus:ring-2 focus:ring-[#3ECF8E] focus:border-transparent outline-none transition-all text-[15px] text-white placeholder-[#808080] disabled:opacity-50"
                        />
                      )}

              <button
                        type="submit"
                        disabled={isEmailLoading}
                        className="w-full py-3 px-4 bg-[#3ECF8E] text-white font-medium text-[15px] rounded-lg hover:bg-[#34B27B] hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isEmailLoading ? (
                          <>
                            <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Inscription en cours...</span>
                          </>
                        ) : (
                          <span>{showPassword ? "S'inscrire" : "Continuer avec l'e-mail"}</span>
                        )}
              </button>
                    </form>

                    {/* Terms */}
                    <p className="mt-4 text-xs text-center text-[#808080] leading-relaxed">
                      En continuant, vous acceptez nos{' '}
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowTermsModal(true)
                          setCurrentPage('terms')
                        }}
                        className="text-[#3ECF8E] hover:underline font-medium"
                      >
                        Conditions d'utilisation
                      </button>
                      {' '}et notre{' '}
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowTermsModal(true)
                          setCurrentPage('privacy')
                        }}
                        className="text-[#3ECF8E] hover:underline font-medium"
                      >
                        Politique de confidentialité
                      </button>
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Right Side - Video */}
            <div className="w-full flex h-full">
              <div className="w-full h-full bg-[#1A1A1A] rounded-2xl border border-[#2C2C2C] overflow-hidden relative">
                <video
                  ref={videoRef}
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls={false}
                  disablePictureInPicture
                  disableRemotePlayback
                  className="w-full h-full object-cover"
                  style={{ minHeight: '100%', pointerEvents: 'none' }}
                  onLoadedData={() => {
                    setVideoLoaded(true)
                    setVideoError(false)
                  }}
                  onError={() => {
                    console.error('Video failed to load.')
                    setVideoError(true)
                    setVideoLoaded(false)
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <source src="/video 2.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {/* Fallback placeholder if video doesn't exist or fails to load */}
                {(!videoLoaded || videoError) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A1A]">
                    <div className="text-center p-8">
                      <svg className="w-24 h-24 text-[#3ECF8E] mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-[#3ECF8E] font-medium text-lg">Vidéo de présentation</p>
                      <p className="text-[#808080] text-sm mt-2">Chargement de la vidéo...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Discover Avocat Section */}
      <div className="w-full py-20 px-4 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          {/* Heading and Description */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Découvrez Avocat
            </h2>
            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-3xl mx-auto leading-relaxed">
              Obtenez des informations juridiques fiables en quelques secondes. Prenez des notes, modifiez-les et analysez-les grâce à l'intelligence artificielle. Toutes vos notes sont ensuite stockées en toute sécurité dans votre espace personnel, le « Vault ».
            </p>
          </div>

          {/* Image Placeholder and Feature Buttons */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Side - Image Placeholder */}
            <div className="bg-[#1A1A1A] rounded-2xl border border-[#2C2C2C] overflow-hidden relative inline-block">
              {selectedImage ? (
                <div className="overflow-hidden relative" style={{ width: '100%' }}>
                  <img 
                    src={selectedImage} 
                    alt="Feature preview" 
                    className="block"
                    style={{ 
                      width: '125%',
                      height: 'auto',
                      objectPosition: 'left center',
                      transform: 'translateX(0)'
                    }}
                  />
                </div>
              ) : (
                <div className="text-center p-8 min-h-[500px] flex items-center justify-center w-full">
                  <div>
                    <svg className="w-24 h-24 text-[#3ECF8E] mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-[#808080] text-sm">Cliquez sur un bouton pour voir l'aperçu</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Feature Buttons */}
            <div className="space-y-4">
              {/* Feature Button 1 */}
              <button 
                onClick={() => setSelectedImage('/luck.jpg')}
                className="w-full flex items-center gap-4 p-6 bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group text-left"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#3ECF8E]/10 flex items-center justify-center group-hover:bg-[#3ECF8E]/20 transition-colors">
                  <svg className="w-6 h-6 text-[#3ECF8E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">Réponses juridiques instantanées</h3>
                  <p className="text-[#B3B3B3] text-sm">Posez vos questions juridiques et obtenez des réponses fiables en quelques secondes.</p>
                </div>
              </button>

              {/* Feature Button 2 */}
              <button 
                onClick={() => {}}
                className="w-full flex items-center gap-4 p-6 bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group text-left"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#3ECF8E]/10 flex items-center justify-center group-hover:bg-[#3ECF8E]/20 transition-colors">
                  <svg className="w-6 h-6 text-[#3ECF8E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">Notes intelligentes</h3>
                  <p className="text-[#B3B3B3] text-sm">Créez et modifiez vos notes facilement. Analysez-les ensuite grâce à l'intelligence artificielle pour mieux organiser et comprendre vos informations.</p>
                </div>
              </button>

              {/* Feature Button 3 */}
              <button className="w-full flex items-center gap-4 p-6 bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group text-left">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#3ECF8E]/10 flex items-center justify-center group-hover:bg-[#3ECF8E]/20 transition-colors">
                  <svg className="w-6 h-6 text-[#3ECF8E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">Mon Vault</h3>
                  <p className="text-[#B3B3B3] text-sm">Enregistrez vos notes et analyses en toute sécurité. Votre Vault garantit que toutes vos informations restent privées et accessibles à tout moment</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="w-full py-20 px-4 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-12">
            Foire aux questions
          </h2>

          <div className="space-y-4">
            {/* FAQ Item 1 */}
            <div className="bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFAQ(openFAQ === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-[#1F1F1F] transition-colors"
              >
                <h3 className="text-lg font-semibold text-white pr-4">
                  À qui s'adresse cette plateforme ?
                </h3>
                <svg
                  className={`w-6 h-6 text-[#3ECF8E] flex-shrink-0 transition-transform duration-200 ${
                    openFAQ === 1 ? 'rotate-45' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              {openFAQ === 1 && (
                <div className="px-6 pb-6">
                  <p className="text-[#B3B3B3] leading-relaxed">
                    Notre plateforme s'adresse aux particuliers, entrepreneurs, étudiants et professionnels souhaitant obtenir rapidement des informations juridiques fiables, comprendre leurs droits et mieux gérer leurs démarches légales, simplement et efficacement.
                  </p>
                </div>
              )}
            </div>

            {/* FAQ Item 2 */}
            <div className="bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFAQ(openFAQ === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-[#1F1F1F] transition-colors"
              >
                <h3 className="text-lg font-semibold text-white pr-4">
                  Comment la plateforme m'aide-t-elle concrètement ?
                </h3>
                <svg
                  className={`w-6 h-6 text-[#3ECF8E] flex-shrink-0 transition-transform duration-200 ${
                    openFAQ === 2 ? 'rotate-45' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              {openFAQ === 2 && (
                <div className="px-6 pb-6">
                  <p className="text-[#B3B3B3] leading-relaxed">
                    Elle vous permet de poser des questions juridiques et d'obtenir des réponses instantanées basées sur les lois locales. Vous pouvez également créer, modifier et analyser vos notes grâce à l'intelligence artificielle, puis les conserver en toute sécurité dans votre Vault personnel.
                  </p>
                </div>
              )}
            </div>

            {/* FAQ Item 3 */}
            <div className="bg-[#1A1A1A] border border-[#2C2C2C] rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFAQ(openFAQ === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-[#1F1F1F] transition-colors"
              >
                <h3 className="text-lg font-semibold text-white pr-4">
                  Pourquoi la plateforme est-elle gratuite pour le moment ?
                </h3>
                <svg
                  className={`w-6 h-6 text-[#3ECF8E] flex-shrink-0 transition-transform duration-200 ${
                    openFAQ === 3 ? 'rotate-45' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              {openFAQ === 3 && (
                <div className="px-6 pb-6">
                  <p className="text-[#B3B3B3] leading-relaxed">
                    La plateforme est actuellement proposée gratuitement dans le cadre d'une phase de test afin de permettre aux utilisateurs de découvrir ses fonctionnalités et de contribuer à son amélioration. Vos retours sont précieux pour nous aider à offrir une expérience toujours plus performante et fiable.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-black border-t border-[#2C2C2C] py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Logo and Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="/logo_supabase_green.png" 
                  alt="Avocat AI" 
                  className="h-8 w-auto"
                />
                <span className="text-xl font-semibold text-white">Avocat AI</span>
              </div>
              <p className="text-[#808080] text-sm mb-4">
                La première plateforme juridique IA en Tunisie — performante, éco-optimisée et responsable.
              </p>
              {/* Social Media Links */}
              <div className="flex items-center gap-3">
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-[#2C2C2C] flex items-center justify-center hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group"
                  aria-label="LinkedIn"
                >
                  <svg className="w-5 h-5 text-[#808080] group-hover:text-[#3ECF8E] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-[#2C2C2C] flex items-center justify-center hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group"
                  aria-label="GitHub"
                >
                  <svg className="w-5 h-5 text-[#808080] group-hover:text-[#3ECF8E] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-[#2C2C2C] flex items-center justify-center hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group"
                  aria-label="X (Twitter)"
                >
                  <svg className="w-5 h-5 text-[#808080] group-hover:text-[#3ECF8E] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-[#2C2C2C] flex items-center justify-center hover:bg-[#1F1F1F] hover:border-[#3ECF8E]/40 transition-all duration-200 group"
                  aria-label="YouTube"
                >
                  <svg className="w-5 h-5 text-[#808080] group-hover:text-[#3ECF8E] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Links Column 1 - Plateforme */}
            <div>
              <h4 className="text-white font-semibold mb-4">Plateforme</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Réponses juridiques instantanées
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Notes intelligentes
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Mon Vault
                  </button>
                </li>
              </ul>
            </div>

            {/* Links Column 2 - Solutions */}
            <div>
              <h4 className="text-white font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Recherche d'articles en quelques secondes
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Synthèse automatique de vos conversations
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Analyse juridique
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Organisation de vos dossiers et historique
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {}}
                    className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors text-left"
                  >
                    Assistant IA disponible 24h/24
                  </button>
                </li>
              </ul>
            </div>

          </div>

          {/* Legal Links Section */}
          <div className="pt-8 border-t border-[#2C2C2C]">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6 justify-center items-center">
              <a
                href="/terms"
                className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors"
              >
                Conditions d'utilisation
              </a>
              <span className="hidden md:inline text-[#2C2C2C]">|</span>
              <a
                href="/privacy"
                className="text-[#808080] hover:text-[#3ECF8E] text-sm transition-colors"
              >
                Politique de confidentialité
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-[#2C2C2C]">
            <p className="text-[#808080] text-sm text-center">
              © {new Date().getFullYear()} Avocat AI. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms and Privacy Modal */}
      {showTermsModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4" 
          style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowTermsModal(false)}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />
          
          {/* Modal Container */}
          <div 
            className="relative bg-[#1A1A1A] rounded-2xl border border-[#2C2C2C] shadow-2xl w-full max-w-3xl max-h-[90vh] min-h-[500px] flex flex-col overflow-hidden backdrop-blur-xl"
            style={{ position: 'relative', zIndex: 10000, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#2C2C2C] bg-gradient-to-r from-[#1A1A1A] to-[#1F1F1F]">
              <h2 className="text-xl font-semibold text-white tracking-tight">
                {currentPage === 'privacy' ? 'Politique de Confidentialité' : "Conditions d'utilisation"}
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[#252525] text-[#808080] hover:text-white transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content Area with Slide Transition */}
            <div className="flex-1 overflow-hidden relative">
              {/* Privacy Policy Page */}
              <div 
                className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
                  currentPage === 'privacy' ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 pointer-events-none'
                }`}
              >
                {/* Scrollable Content */}
                <div className="h-full overflow-y-auto px-6 py-6 modal-scroll" style={{ paddingBottom: '100px' }}>
                <div className="prose prose-invert max-w-none">
                  <h1 className="text-2xl font-bold text-white mb-4">POLITIQUE DE CONFIDENTIALITÉ</h1>
                  <p className="text-[#B3B3B3] mb-6">Dernière mise à jour : 11/02/2026</p>
                  
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">1. QUI SOMMES-NOUS ?</h2>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      Avocat AI est Notre plateforme juridique intelligente, propulsée par l'intelligence artificielle, permet d'obtenir des réponses claires, fiables et instantanées à vos questions juridiques, elle facilite l'accès au droit en fournissant des informations juridiques précises en quelques secondes. Grâce à une technologie avancée et une base de connaissances rigoureusement sélectionnée, notre solution simplifie la compréhension des textes légaux, oriente les utilisateurs dans leurs démarches et contribue à une meilleure prise de décision, en toute sécurité et confidentialité.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">2. QUELLES DONNÉES COLLECTONS-NOUS ?</h2>
                    <p className="text-[#B3B3B3] mb-2">Tu nous fournis :</p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-4 space-y-1">
                      <li>Ton email et ton nom (si tu crées un compte)</li>
                    </ul>
                    <p className="text-[#B3B3B3] mb-2">On collecte automatiquement :</p>
                    <ul className="list-disc list-inside text-[#B3B3B3] space-y-1">
                      <li>Ton adresse IP</li>
                      <li>La date et l'heure de tes connexions</li>
                    </ul>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">3. POURQUOI ON COLLECTE CES DONNÉES ?</h2>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-[#2C2C2C]">
                        <thead>
                          <tr className="bg-[#252525]">
                            <th className="border border-[#2C2C2C] px-4 py-2 text-left text-white font-semibold">Donnée</th>
                            <th className="border border-[#2C2C2C] px-4 py-2 text-left text-white font-semibold">Pourquoi ?</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-[#2C2C2C] px-4 py-2 text-[#B3B3B3]">Email, compte</td>
                            <td className="border border-[#2C2C2C] px-4 py-2 text-[#B3B3B3]">Pour te connecter et gérer ton abonnement</td>
                          </tr>
                          <tr>
                            <td className="border border-[#2C2C2C] px-4 py-2 text-[#B3B3B3]">IP, logs</td>
                            <td className="border border-[#2C2C2C] px-4 py-2 text-[#B3B3B3]">Pour sécuriser l'application</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">4. CONSERVATION DES DONNÉES</h2>
                    <ul className="list-disc list-inside text-[#B3B3B3] space-y-1">
                      <li>Comptes actifs : conservés tant que tu utilises le service</li>
                      <li>Données de connexion : conservées 1 an</li>
                      <li>Après ces délais, tout est supprimé.</li>
                    </ul>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">5. ATTENTION - DONNÉES SENSIBLES</h2>
                    <p className="text-[#B3B3B3] mb-3">Ne partage JAMAIS ces informations dans tes conversations :</p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-3 space-y-1">
                      <li>Numéro de carte d'identité ou passeport</li>
                      <li>Informations médicales</li>
                      <li>Opinions politiques ou religieuses</li>
                      <li>Données bancaires</li>
                    </ul>
                    <p className="text-[#B3B3B3] mb-2"><strong className="text-white">Pourquoi ?</strong> Parce qu'on ne veut pas avoir à gérer ce type de données, et c'est plus sûr pour toi.</p>
                    <p className="text-[#B3B3B3]">Si tu en envoies quand même, on les supprime automatiquement dès qu'on les détecte.</p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">6. TES DROITS</h2>
                    <p className="text-[#B3B3B3] mb-3">Tu peux à tout moment :</p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-3 space-y-1">
                      <li>Voir les données qu'on a sur toi</li>
                      <li>Demander une correction</li>
                      <li>Demander la suppression de tes données</li>
                      <li>Retirer ton consentement</li>
                    </ul>
                    <p className="text-[#B3B3B3]"><strong className="text-white">Comment ?</strong> Envoie-nous un email. On traite ta demande sous 30 jours maximum.</p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">7. SÉCURITÉ</h2>
                    <p className="text-[#B3B3B3] mb-3">
                      Tous tes échanges sont chiffrés (SSL). Tes questions et les réponses de l'IA voyagent de manière cryptée entre ton appareil et nos serveurs, comme un tunnel privé que personne ne peut ouvrir. Seuls toi et l'application avez la clé.
                    </p>
                    <p className="text-[#B3B3B3]">
                      Tes conversations sont stockées de façon sécurisée et ne sont accessibles qu'aux personnes strictement habilitées à gérer le service. Nous ne partageons aucune donnée avec des annonceurs ou des tiers commerciaux.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">8. Consentement</h2>
                    <p className="text-[#B3B3B3]">
                      La plateforme est actuellement mise à disposition gratuitement. Les données que vous saisissez peuvent être partagées avec des services d'intelligence artificielle externes afin de générer des réponses et améliorer le fonctionnement de la plateforme. L'utilisation gratuite implique que vos données sont uniquement utilisées dans ce cadre et ne servent pas à d'autres fins.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">9. MODIFICATIONS</h2>
                    <p className="text-[#B3B3B3]">
                      On peut mettre à jour cette politique. Si on change des choses importantes, on te préviendra par email ou par notification.
                    </p>
                  </section>
                </div>
                </div>
              </div>

              {/* Terms of Use Page */}
              <div 
                className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
                  currentPage === 'terms' ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                }`}
              >
                {/* Scrollable Content */}
                <div className="h-full overflow-y-auto px-6 py-6 modal-scroll" style={{ paddingBottom: '100px' }}>
                <div className="prose prose-invert max-w-none">
                  <h1 className="text-2xl font-bold text-white mb-4">CONDITIONS D'UTILISATION</h1>
                  <p className="text-[#B3B3B3] mb-2">Application : Avocat AI</p>
                  <p className="text-[#B3B3B3] mb-6">Dernière mise à jour : 11/02/2026</p>
                  
                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">1. ACCEPTATION DES CONDITIONS</h2>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      En créant un compte et en utilisant Avocat AI, tu acceptes pleinement les présentes conditions.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">2. NATURE DU SERVICE</h2>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Avocat AI est un outil d'information juridique basé sur l'intelligence artificielle.
                    </p>
                    <p className="text-[#B3B3B3] mb-2"><strong className="text-white">Ce que nous ne sommes pas :</strong></p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-4 space-y-1">
                      <li>Un cabinet d'avocats</li>
                      <li>Un conseil juridique professionnel</li>
                      <li>Une assistance en justice</li>
                    </ul>
                    <p className="text-[#B3B3B3] mb-2"><strong className="text-white">Ce que nous sommes :</strong></p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-3 space-y-1">
                      <li>Un assistant conversationnel qui t'aide à comprendre le droit tunisien</li>
                      <li>Un outil de recherche d'articles de loi et de textes juridiques</li>
                      <li>Une aide à la veille documentaire</li>
                    </ul>
                    <p className="text-[#B3B3B3] italic">*Aucune relation avocat-client n'est créée entre toi et l'application.</p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">3. LIMITES DE L'INTELLIGENCE ARTIFICIELLE</h2>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      L'IA peut se tromper. Elle peut fournir des informations :
                    </p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-3 space-y-1">
                      <li>Incomplètes</li>
                      <li>Imprécises</li>
                      <li>Partiellement obsolètes</li>
                    </ul>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Tu es seul responsable de l'utilisation que tu fais des réponses.
                    </p>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      Pour toute décision importante (contractuelle, judiciaire, administrative), consulte obligatoirement un avocat inscrit au barreau tunisien.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">4. UTILISATION AUTORISÉE</h2>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Tu as le droit d'utiliser l'application pour un usage strictement personnel et non commercial.
                    </p>
                    <p className="text-[#B3B3B3] mb-2"><strong className="text-white">Il t'est interdit de :</strong></p>
                    <ul className="list-disc list-inside text-[#B3B3B3] space-y-1">
                      <li>Copier, redistribuer ou vendre les réponses générées</li>
                      <li>Utiliser le service à des fins illégales</li>
                      <li>Tenter de contourner les mesures de sécurité</li>
                      <li>Créer plusieurs comptes pour abuser du service</li>
                    </ul>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">5. COMPTE ET SÉCURITÉ</h2>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Tu es responsable de la confidentialité de ton mot de passe.
                    </p>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Si tu constates une utilisation non autorisée de ton compte, préviens-nous immédiatement à [email contact].
                    </p>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      Nous nous réservons le droit de suspendre ou supprimer tout compte en cas de violation des présentes conditions.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">6. RESPONSABILITÉ</h2>
                    <p className="text-[#B3B3B3] mb-2">Notre responsabilité ne peut pas être engagée pour :</p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-3 space-y-1">
                      <li>Les erreurs ou omissions dans les réponses générées</li>
                      <li>Les décisions que tu prends sur la base de ces informations</li>
                      <li>Les dommages indirects (perte de temps, perte d'argent, préjudice moral)</li>
                      <li>L'interruption temporaire du service</li>
                    </ul>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      En clair : on fait de notre mieux, mais on n'est pas parfaits. Tu utilises l'application à tes risques et périls.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">7. PROPRIÉTÉ INTELLECTUELLE</h2>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      L'application, son code, son design, sa marque et sa base de données juridique sont la propriété exclusive de Avocat AI
                    </p>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      Tu ne peux pas reproduire, modifier ou distribuer ces éléments sans autorisation écrite.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">8. FACTURATION ET REMBOURSEMENT</h2>
                    <h3 className="text-lg font-semibold text-white mb-2 mt-4">8.1 Aucun remboursement</h3>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Une fois le paiement effectué, aucun remboursement n'est accordé, quelle qu'en soit la raison.
                    </p>
                    <p className="text-[#B3B3B3] mb-4 leading-relaxed">
                      L'abonnement est payé d'avance. Le service est considéré comme consommé dès l'accès accordé.
                    </p>
                    <h3 className="text-lg font-semibold text-white mb-2">8.2 Suppression du compte = perte de l'abonnement</h3>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      Si tu supprimes ton compte, ton abonnement est immédiatement perdu et définitivement résilié.
                    </p>
                    <p className="text-[#B3B3B3] mb-4 leading-relaxed">
                      Aucun remboursement ni transfert vers un autre compte n'est possible.
                    </p>
                    <ul className="list-disc list-inside text-[#B3B3B3] space-y-1">
                      <li>L'application est actuellement gratuite. Nous nous réservons le droit de la rendre payante à l'avenir, avec une information préalable des utilisateurs</li>
                      <li>Pour les abonnements sont facturés mensuellement ou annuellement, sans engagement. Aucun remboursement n'est possible sauf dysfonctionnement technique avéré de notre part.</li>
                    </ul>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">9. SUSPENSION ET RÉSILIATION</h2>
                    <p className="text-[#B3B3B3] mb-2">On peut suspendre ou résilier ton compte à tout moment si :</p>
                    <ul className="list-disc list-inside text-[#B3B3B3] mb-3 space-y-1">
                      <li>Tu violes les présentes conditions</li>
                      <li>Tu utilises le service de manière abusive</li>
                    </ul>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      Tu peux aussi supprimer ton compte à tout moment depuis les paramètres.
                    </p>
                  </section>

                  <section className="mb-6">
                    <h2 className="text-xl font-semibold text-white mb-3">10. MODIFICATIONS DES CONDITIONS</h2>
                    <p className="text-[#B3B3B3] mb-3 leading-relaxed">
                      On peut modifier ces conditions à tout moment, sans préavis.
                    </p>
                    <p className="text-[#B3B3B3] leading-relaxed">
                      Si tu continues à utiliser l'application après une modification, tu es réputé avoir accepté les nouvelles conditions.
                    </p>
                  </section>
                </div>
                </div>
              </div>
            </div>

            {/* Fixed Action Buttons Footer */}
            <div className="border-t border-[#2C2C2C] bg-gradient-to-r from-[#1A1A1A] to-[#1F1F1F] px-6 py-4 backdrop-blur-sm">
              {currentPage === 'privacy' ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setCurrentPage('terms')
                    }}
                    className="flex-1 px-6 py-3 bg-[#3ECF8E] text-white font-medium rounded-lg hover:bg-[#34B27B] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#3ECF8E]/20 hover:shadow-[#3ECF8E]/30"
                  >
                    Accepter
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage('terms')
                    }}
                    className="flex-1 px-6 py-3 bg-[#1F1F1F] border border-[#2C2C2C] text-white font-medium rounded-lg hover:bg-[#252525] hover:border-[#3C3C3C] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Refuser
                  </button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowTermsModal(false)
                    }}
                    className="flex-1 px-6 py-3 bg-[#3ECF8E] text-white font-medium rounded-lg hover:bg-[#34B27B] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#3ECF8E]/20 hover:shadow-[#3ECF8E]/30"
                  >
                    Accepter
                  </button>
                  <button
                    onClick={() => {
                      setShowTermsModal(false)
                    }}
                    className="flex-1 px-6 py-3 bg-[#1F1F1F] border border-[#2C2C2C] text-white font-medium rounded-lg hover:bg-[#252525] hover:border-[#3C3C3C] transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Refuser
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
